import React, { useMemo, useState } from 'react';

// A single normalized order line for the compare (both "current" and "proposed"
// sides are reduced to this shape by the parent). `line` (the NetSuite line number)
// is the primary alignment key so lines that share an item_id — e.g. several DIS*
// discount lines on the same item — still align 1:1; item_id/name are the fallback
// for rep-added rows with no NS line yet. name/qty/rate drive display + change kind.
export interface DiffLine {
  line?: number | null;
  item_id: number | null;
  name: string;
  qty: number;
  rate: number | null;
}

type ChangeKind = 'added' | 'removed' | 'qty' | 'price' | 'qty_price' | 'unchanged';

interface DiffRow {
  key: string;
  name: string;
  kind: ChangeKind;
  before: DiffLine | null;
  after: DiffLine | null;
}

interface OrderDiffPanelProps {
  // The order's CURRENT lines in NetSuite (the pristine baseline).
  before: DiffLine[];
  // What the order will BECOME once the staged/edited changes are submitted.
  after: DiffLine[];
  // Header label for the current order (e.g. "SO119324").
  soLabel?: string | null;
}

const norm = (s?: string | null) => (s || '').trim().toLowerCase();

const fmtQty = (q: number) => {
  const n = Number(q) || 0;
  return Number.isInteger(n) ? String(n) : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const fmtRate = (r: number | null) =>
  r == null ? '—' : `$${Number(r).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Build aligned diff rows from the two sides. Key on the NetSuite LINE NUMBER first
// (both sides carry it for existing lines, so duplicate-item_id credit lines still
// align 1:1); include item_id so swapping the item on a line reads as remove+add
// rather than a silently-unchanged row. Rep-added rows have no line number yet, so
// they fall back to item_id, then a normalized name.
function buildRows(before: DiffLine[], after: DiffLine[]): DiffRow[] {
  const keyOf = (l: DiffLine) =>
    l.line != null ? `ln:${l.line}:${l.item_id ?? ''}`
    : l.item_id != null ? `id:${l.item_id}`
    : `nm:${norm(l.name)}`;
  const beforeByKey = new Map<string, DiffLine>();
  before.forEach((l) => beforeByKey.set(keyOf(l), l));
  const afterByKey = new Map<string, DiffLine>();
  after.forEach((l) => afterByKey.set(keyOf(l), l));

  const rows: DiffRow[] = [];
  const seen = new Set<string>();

  // Walk the AFTER set first (preserves the proposed order's line ordering), then
  // append any BEFORE lines that were removed.
  after.forEach((a) => {
    const k = keyOf(a);
    seen.add(k);
    const b = beforeByKey.get(k) || null;
    if (!b) {
      rows.push({ key: k, name: a.name, kind: 'added', before: null, after: a });
      return;
    }
    const qtyChanged = (Number(b.qty) || 0) !== (Number(a.qty) || 0);
    const priceChanged = (b.rate ?? null) !== (a.rate ?? null);
    let kind: ChangeKind = 'unchanged';
    if (qtyChanged && priceChanged) kind = 'qty_price';
    else if (qtyChanged) kind = 'qty';
    else if (priceChanged) kind = 'price';
    rows.push({ key: k, name: a.name || b.name, kind, before: b, after: a });
  });
  before.forEach((b) => {
    const k = keyOf(b);
    if (seen.has(k)) return;
    rows.push({ key: k, name: b.name, kind: 'removed', before: b, after: null });
  });
  return rows;
}

const KIND_META: Record<Exclude<ChangeKind, 'unchanged'>, { label: string; badge: string; row: string }> = {
  added: {
    label: 'Added',
    badge: 'bg-green-100 text-green-800 border-green-300',
    row: 'bg-green-50/70',
  },
  removed: {
    label: 'Removed',
    badge: 'bg-red-100 text-red-800 border-red-300',
    row: 'bg-red-50/70',
  },
  qty: {
    label: 'Qty',
    badge: 'bg-amber-100 text-amber-800 border-amber-300',
    row: 'bg-amber-50/70',
  },
  price: {
    label: 'Price',
    badge: 'bg-amber-100 text-amber-800 border-amber-300',
    row: 'bg-amber-50/70',
  },
  qty_price: {
    label: 'Qty + Price',
    badge: 'bg-amber-100 text-amber-800 border-amber-300',
    row: 'bg-amber-50/70',
  },
};

const OrderDiffPanel: React.FC<OrderDiffPanelProps> = ({ before, after, soLabel }) => {
  const [open, setOpen] = useState(true);
  const [showUnchanged, setShowUnchanged] = useState(false);

  const rows = useMemo(() => buildRows(before || [], after || []), [before, after]);
  const changed = rows.filter((r) => r.kind !== 'unchanged');
  const unchanged = rows.filter((r) => r.kind === 'unchanged');

  const counts = useMemo(() => {
    const c = { added: 0, removed: 0, changed: 0 };
    changed.forEach((r) => {
      if (r.kind === 'added') c.added += 1;
      else if (r.kind === 'removed') c.removed += 1;
      else c.changed += 1;
    });
    return c;
  }, [changed]);

  const label = soLabel || 'the current order';
  const summary =
    changed.length === 0
      ? `Matches ${label} — no changes yet`
      : [
          counts.added ? `+${counts.added} added` : '',
          counts.removed ? `${counts.removed} removed` : '',
          counts.changed ? `${counts.changed} changed` : '',
        ]
          .filter(Boolean)
          .join(' · ');

  const renderCell = (l: DiffLine | null) =>
    l == null ? (
      <span className="text-gray-400">—</span>
    ) : (
      <span>
        <span className="font-medium">{fmtQty(l.qty)}</span>
        <span className="text-gray-400"> × </span>
        <span>{fmtRate(l.rate)}</span>
      </span>
    );

  const rowsToShow = showUnchanged ? [...changed, ...unchanged] : changed;

  return (
    <div className="border border-gray-200 rounded-md bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-left"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <svg
            className={`w-3.5 h-3.5 flex-shrink-0 text-gray-500 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
            Compare to {label}
          </span>
          <span
            className={`text-[11px] truncate ${changed.length ? 'text-amber-700' : 'text-gray-400'}`}
          >
            {summary}
          </span>
        </div>
      </button>

      {open && (
        <div className="px-2.5 pb-2">
          {changed.length === 0 ? (
            <div className="text-xs text-gray-500 py-1">
              The proposed order is identical to {label}. Make a change above and it will show
              here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="text-gray-500">
                    <th className="text-left font-medium py-1 pr-2">Item</th>
                    <th className="text-right font-medium py-1 px-2 whitespace-nowrap">
                      Current ({label})
                    </th>
                    <th className="w-4"></th>
                    <th className="text-right font-medium py-1 px-2 whitespace-nowrap">Proposed</th>
                    <th className="text-right font-medium py-1 pl-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rowsToShow.map((r) => {
                    const meta = r.kind === 'unchanged' ? null : KIND_META[r.kind];
                    return (
                      <tr
                        key={r.key}
                        className={`border-t border-gray-100 ${meta ? meta.row : ''}`}
                      >
                        <td className="py-1 pr-2 align-top">
                          <span
                            className={
                              r.kind === 'removed' ? 'line-through text-gray-500' : 'text-gray-800'
                            }
                          >
                            {r.name || '(unnamed item)'}
                          </span>
                        </td>
                        <td className="py-1 px-2 text-right align-top text-gray-600 whitespace-nowrap">
                          {renderCell(r.before)}
                        </td>
                        <td className="py-1 text-center align-top text-gray-300">→</td>
                        <td className="py-1 px-2 text-right align-top text-gray-800 whitespace-nowrap">
                          {renderCell(r.after)}
                        </td>
                        <td className="py-1 pl-2 text-right align-top">
                          {meta && (
                            <span
                              className={`inline-block text-[10px] leading-none px-1.5 py-0.5 rounded border ${meta.badge}`}
                            >
                              {meta.label}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {unchanged.length > 0 && (
            <button
              type="button"
              onClick={() => setShowUnchanged((v) => !v)}
              className="mt-1.5 text-[11px] text-brand-600 hover:text-brand-700 font-medium"
            >
              {showUnchanged
                ? 'Hide unchanged lines'
                : `Show ${unchanged.length} unchanged line${unchanged.length === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderDiffPanel;
